const RELATIVE_FAMILIES = new Set(["M", "Q", "H", "S", "C"]);
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const RELATIVE = /^([A-Z])\+(\d+)$/;
const MONTH = /^([A-Z]{3})(\d{2})$/;
const QUARTER_OR_HALF = /^([QH])(\d)(\d{2})$/;
const SEASON = /^(SUM|WIN)(\d{2})$/;
const CALENDAR = /^CAL(\d{2})$/;
const EXAMPLES = 'a period like "Mar26", "Q126", "Sum26" or "Cal26", or a relative offset like "M+1"';
const titleCase = (word) => word.charAt(0) + word.slice(1).toLowerCase();
/** Spaces and hyphens are decoration: `"mAR-26"` and `"Q1 26"` mean `"Mar26"` and `"Q126"`. */
const strip = (input) => input.replace(/[\s-]/g, "").toUpperCase();
function parseAbsolute(code) {
    const month = MONTH.exec(code);
    const monthIndex = month ? MONTHS.indexOf(month[1]) : -1;
    if (month && monthIndex !== -1) {
        return {
            kind: "month",
            month: monthIndex + 1,
            year: Number(month[2]),
            label: `${titleCase(month[1])}${month[2]}`,
        };
    }
    const period = QUARTER_OR_HALF.exec(code);
    if (period) {
        const kind = period[1] === "Q" ? "quarter" : "half";
        const index = Number(period[2]);
        if (index < 1 || index > (kind === "quarter" ? 4 : 2))
            return null;
        return { kind, index, year: Number(period[3]), label: `${period[1]}${index}${period[3]}` };
    }
    const season = SEASON.exec(code);
    if (season) {
        const name = titleCase(season[1]);
        return { kind: "season", season: name, year: Number(season[2]), label: `${name}${season[2]}` };
    }
    const calendar = CALENDAR.exec(code);
    if (calendar) {
        return { kind: "calendar", year: Number(calendar[1]), label: `Cal${calendar[1]}` };
    }
    return null;
}
/**
 * @internal Parse an offset into its parts, or return the reason it is not one. The single
 * parser for the offset grammar: {@link validateOffset} is a thin wrapper, and resolvers
 * that map an offset onto storage consume this rather than re-reading the string.
 */
export function parseOffset(offset) {
    if (typeof offset === "number") {
        if (!Number.isInteger(offset) || offset < 0) {
            return { error: `invalid offset ${offset}: a numeric offset must be a non-negative whole number of months` };
        }
        return { parsed: { kind: "relative", family: "M", index: offset } };
    }
    if (typeof offset !== "string")
        return { error: `invalid offset: expected ${EXAMPLES}` };
    const code = strip(offset);
    if (code === "")
        return { error: `invalid offset: expected ${EXAMPLES}` };
    if (/^\d+$/.test(code))
        return { parsed: { kind: "relative", family: "M", index: Number(code) } };
    if (code.includes("+")) {
        const relative = RELATIVE.exec(code);
        if (!relative || !RELATIVE_FAMILIES.has(relative[1])) {
            return { error: `invalid relative offset "${offset}": expected M+N, Q+N, H+N, S+N or C+N` };
        }
        return { parsed: { kind: "relative", family: relative[1], index: Number(relative[2]) } };
    }
    const period = parseAbsolute(code);
    if (!period)
        return { error: `unknown offset "${offset}": expected ${EXAMPLES}` };
    return { parsed: { kind: "absolute", period } };
}
/** @internal The canonical spelling of an already-parsed offset. */
export function formatOffset(parsed) {
    return parsed.kind === "relative" ? `${parsed.family}+${parsed.index}` : parsed.period.label;
}
/**
 * Check an {@link Offset} against the grammar and return its canonical spelling.
 *
 * Input is forgiving: matching is case-insensitive and spaces and hyphens are dropped,
 * so `"mAR-26"`, `"MAR26"` and `"mar 26"` all normalize to `"Mar26"`. Validation is
 * syntax only, so an offset beyond the stored window normalizes cleanly and resolves
 * to `null` later.
 *
 * @example
 * ```ts
 * artis.v1.offsets.validate("q1-26");  // { normalized: "Q126" }
 * artis.v1.offsets.validate(5);        // { normalized: "M+5" }
 * artis.v1.offsets.validate("M-1");    // { error: 'unknown offset "M-1": expected …' }
 * ```
 */
export function validateOffset(offset) {
    const { parsed, error } = parseOffset(offset);
    return parsed ? { normalized: formatOffset(parsed) } : { error };
}
//# sourceMappingURL=offset.js.map